<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getUnixTime } from 'date-fns';
import { useStore, useFunctionGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useInbox } from 'dashboard/composables/useInbox';
import { useScheduledMessages } from 'dashboard/composables/useScheduledMessages';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import wootConstants from 'dashboard/constants/globals';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const conversationId = ref(null);
const templateParserRef = ref(null);
const selectedTemplate = ref(null);
const isTemplateMode = ref(false);

const form = reactive({
  dateValue: '',
  timeValue: '',
  content: '',
  reopenConversation: true,
  snoozeNow: true,
});

const { isCreating, createScheduledMessage } =
  useScheduledMessages(conversationId);

const { inbox, isAPIInbox, isAWhatsAppChannel, isAnEvolutionWhatsAppChannel } =
  useInbox();

const inboxId = computed(() => inbox.value?.id);

const templates = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  inboxId
);

const supportsTemplates = computed(() => !!templates.value?.length);

// Evolution is a WhatsApp Web gateway with no session window, so it takes plain text at any time.
// Meta and 360dialog only accept an approved template once the 24h window closes, which is the
// usual state by the time a scheduled message goes out.
const hasSessionWindow = computed(
  () => isAWhatsAppChannel.value && !isAnEvolutionWhatsAppChannel.value
);

const QUICK_OPTIONS = [
  {
    key: wootConstants.SNOOZE_OPTIONS.AN_HOUR_FROM_NOW,
    labelKey: 'SCHEDULED_MESSAGES.DIALOG.QUICK_OPTIONS.AN_HOUR_FROM_NOW',
  },
  {
    key: wootConstants.SNOOZE_OPTIONS.UNTIL_TOMORROW,
    labelKey: 'SCHEDULED_MESSAGES.DIALOG.QUICK_OPTIONS.TOMORROW',
  },
  {
    key: wootConstants.SNOOZE_OPTIONS.UNTIL_NEXT_WEEK,
    labelKey: 'SCHEDULED_MESSAGES.DIALOG.QUICK_OPTIONS.NEXT_WEEK',
  },
  {
    key: wootConstants.SNOOZE_OPTIONS.UNTIL_NEXT_MONTH,
    labelKey: 'SCHEDULED_MESSAGES.DIALOG.QUICK_OPTIONS.NEXT_MONTH',
  },
];

const pad = n => String(n).padStart(2, '0');

const dateStrOf = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const timeStrOf = date => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

const applyQuickOption = key => {
  const unix = findSnoozeTime(key);
  if (!unix) return;
  const date = new Date(unix * 1000);
  form.dateValue = dateStrOf(date);
  form.timeValue = timeStrOf(date);
};

const todayDateStr = computed(() => dateStrOf(new Date()));
const minTimeStr = computed(() =>
  form.dateValue === todayDateStr.value ? timeStrOf(new Date()) : '00:00'
);

const scheduledAtUnix = computed(() => {
  if (!form.dateValue || !form.timeValue) return null;
  return getUnixTime(new Date(`${form.dateValue}T${form.timeValue}`));
});

const isInPast = computed(
  () =>
    scheduledAtUnix.value !== null &&
    scheduledAtUnix.value <= getUnixTime(new Date())
);

const hasMessage = computed(() =>
  isTemplateMode.value ? !!selectedTemplate.value : !!form.content.trim()
);

const isTemplateFormInvalid = computed(
  () => templateParserRef.value?.isFormInvalid ?? true
);

const messageRequired = computed(() => !form.snoozeNow);

const canSubmit = computed(() => {
  if (!form.dateValue || !form.timeValue || isInPast.value) return false;
  if (!hasMessage.value) return form.snoozeNow;
  if (isTemplateMode.value && isTemplateFormInvalid.value) return false;
  return true;
});

const confirmLabel = computed(() =>
  hasMessage.value
    ? t('SCHEDULED_MESSAGES.DIALOG.CONFIRM_SCHEDULE')
    : t('SCHEDULED_MESSAGES.DIALOG.CONFIRM_SNOOZE')
);

const resetForm = () => {
  form.dateValue = '';
  form.timeValue = '';
  form.content = '';
  form.reopenConversation = true;
  form.snoozeNow = true;
  selectedTemplate.value = null;
  isTemplateMode.value = supportsTemplates.value && hasSessionWindow.value;
};

const open = (id, { reopenConversation = true, snoozeNow = true } = {}) => {
  conversationId.value = id;
  resetForm();
  form.reopenConversation = reopenConversation;
  form.snoozeNow = snoozeNow;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const setMessageMode = useTemplate => {
  isTemplateMode.value = useTemplate;
  selectedTemplate.value = null;
};

const scheduleMessage = async ({ content, templateParams }) => {
  const success = await createScheduledMessage({
    content,
    templateParams,
    scheduledAt: scheduledAtUnix.value,
    reopenConversation: form.reopenConversation,
    snoozeNow: form.snoozeNow,
  });

  // The new schedule shows up at the bottom of the thread behind the dialog, and open() resets the form.
  if (success) close();
};

const handleTemplateSubmit = ({ message, templateParams }) =>
  scheduleMessage({ content: message, templateParams });

const handleConfirm = async () => {
  if (!canSubmit.value) return;

  if (!hasMessage.value) {
    await store.dispatch('toggleStatus', {
      conversationId: conversationId.value,
      status: wootConstants.STATUS_TYPE.SNOOZED,
      snoozedUntil: scheduledAtUnix.value,
    });
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    close();
    return;
  }

  if (isTemplateMode.value) {
    templateParserRef.value?.sendMessage();
    return;
  }

  await scheduleMessage({ content: form.content });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="t('SCHEDULED_MESSAGES.DIALOG.TITLE')"
    :description="t('SCHEDULED_MESSAGES.DIALOG.DESCRIPTION')"
    :confirm-button-label="confirmLabel"
    :cancel-button-label="t('SCHEDULED_MESSAGES.DIALOG.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isCreating"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SCHEDULED_MESSAGES.DIALOG.WHEN') }}
        </label>
        <div class="flex gap-2">
          <Input
            v-model="form.dateValue"
            type="date"
            :min="todayDateStr"
            class="flex-1"
          />
          <Input
            v-model="form.timeValue"
            type="time"
            :min="minTimeStr"
            class="flex-1"
          />
        </div>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="option in QUICK_OPTIONS"
            :key="option.key"
            type="button"
            class="px-2.5 py-1 text-xs rounded-full transition-colors bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-12"
            @click="applyQuickOption(option.key)"
          >
            {{ t(option.labelKey) }}
          </button>
        </div>
        <p v-if="isInPast" class="m-0 text-xs text-n-ruby-9">
          {{ t('SCHEDULED_MESSAGES.DIALOG.PAST_DATE_ERROR') }}
        </p>
      </div>

      <div
        class="flex gap-3 justify-between items-center px-3 py-2.5 rounded-lg bg-n-alpha-1"
      >
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.DIALOG.SNOOZE_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('SCHEDULED_MESSAGES.DIALOG.SNOOZE_HELP') }}
          </span>
        </div>
        <Switch v-model="form.snoozeNow" />
      </div>

      <div
        class="flex gap-3 justify-between items-center px-3 py-2.5 rounded-lg bg-n-alpha-1"
      >
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.DIALOG.REOPEN_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('SCHEDULED_MESSAGES.DIALOG.REOPEN_HELP') }}
          </span>
        </div>
        <Switch v-model="form.reopenConversation" />
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex gap-3 justify-between items-center">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_LABEL') }}
          </label>
          <div
            v-if="supportsTemplates"
            class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1"
          >
            <button
              type="button"
              class="px-2.5 py-1 text-xs rounded-md transition-colors"
              :class="
                isTemplateMode
                  ? 'text-n-slate-11 hover:text-n-slate-12'
                  : 'bg-n-solid-1 text-n-slate-12'
              "
              @click="setMessageMode(false)"
            >
              {{ t('SCHEDULED_MESSAGES.DIALOG.MODE_FREE_TEXT') }}
            </button>
            <button
              type="button"
              class="px-2.5 py-1 text-xs rounded-md transition-colors"
              :class="
                isTemplateMode
                  ? 'bg-n-solid-1 text-n-slate-12'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="setMessageMode(true)"
            >
              {{ t('SCHEDULED_MESSAGES.DIALOG.MODE_TEMPLATE') }}
            </button>
          </div>
        </div>

        <p v-if="hasSessionWindow" class="m-0 text-xs text-n-slate-11">
          {{
            isTemplateMode
              ? t('SCHEDULED_MESSAGES.DIALOG.TEMPLATE_HINT')
              : t('SCHEDULED_MESSAGES.DIALOG.FREE_TEXT_WARNING')
          }}
        </p>

        <template v-if="isTemplateMode">
          <TemplatesPicker
            v-if="!selectedTemplate"
            :inbox-id="inboxId"
            @on-select="template => (selectedTemplate = template)"
          />
          <div v-else class="flex flex-col gap-2">
            <WhatsAppTemplateParser
              ref="templateParserRef"
              :template="selectedTemplate"
              :send-rendered-content="isAPIInbox"
              @send-message="handleTemplateSubmit"
            />
            <div class="flex justify-end">
              <Button
                slate
                faded
                sm
                :label="t('SCHEDULED_MESSAGES.DIALOG.CHANGE_TEMPLATE')"
                @click="selectedTemplate = null"
              />
            </div>
          </div>
        </template>

        <TextArea
          v-else
          v-model="form.content"
          :placeholder="
            messageRequired
              ? t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_PLACEHOLDER_REQUIRED')
              : t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_PLACEHOLDER_OPTIONAL')
          "
          :max-length="1000"
          show-character-count
        />
      </div>
    </div>
  </Dialog>
</template>
